import React, { useState, useEffect, useMemo } from "react";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import Text from "components/common/Text";
import Select from "components/common/Select";
import SectionLoader from "components/common/SectionLoader";

import { getRole } from "services/rbac";

import PermissionPicker from "./PermissionPicker";

import "./RoleDialog.scss";

const USER_TYPE_OPTIONS = [
  { value: "edge", label: "Edge" },
  { value: "customer", label: "Customer" },
  { value: "employee", label: "Employee" },
];

// props: { open, mode: "create"|"edit", role, catalog, onClose, onSubmit }
export default function RoleDialog({ open, mode, role, catalog, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [userType, setUserType] = useState("");
  const [description, setDescription] = useState("");
  const [permissionKeys, setPermissionKeys] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Only edge callers may create edge roles; non-edge callers are limited to customer/employee.
  const isEdgeCaller = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("loggedInUser") || "{}").type === "edge";
    } catch {
      return false;
    }
  }, []);

  const userTypeOptions = useMemo(
    () => (isEdgeCaller ? USER_TYPE_OPTIONS : USER_TYPE_OPTIONS.filter((o) => o.value !== "edge")),
    [isEdgeCaller]
  );

  useEffect(() => {
    if (!open) return undefined;
    if (mode === "edit" && role) {
      setName(role.Name || "");
      setUserType(role.User_Type || "");
      setDescription(role.Description || "");

      // The role list rows never include current permission keys (GET /v4/roles omits them),
      // so fetch the role's real permissions from GET /v4/roles/:roleId instead of trusting
      // a (nonexistent) role.permissionKeys field. Show a loading state in the meantime so a
      // user can't mistake "not loaded yet" for "no permissions" and wipe them out on save.
      let cancelled = false;
      setPermissionKeys([]);
      setLoadingPermissions(true);
      getRole(role.Role_ID)
        .then((result) => {
          if (cancelled) return;
          setPermissionKeys(result?.permissionKeys || []);
        })
        .catch(() => {
          // getRole normally fails soft (res.ok === false -> null), matching every
          // other read in this service. This guards against an unexpected rejection
          // so it never surfaces as an unhandled promise rejection.
        })
        .finally(() => {
          if (!cancelled) setLoadingPermissions(false);
        });

      return () => {
        cancelled = true;
      };
    }
    setName("");
    setUserType("");
    setDescription("");
    setPermissionKeys([]);
    setLoadingPermissions(false);
    return undefined;
  }, [open, mode, role]);

  const canSubmit = name.trim() !== "" && !!userType && !loadingPermissions;

  // User type is immutable in edit mode (changes permission applicability); changing it resets permissions.
  const handleUserTypeChange = (value) => {
    setUserType(value);
    setPermissionKeys([]);
  };

  const submit = () => {
    onSubmit({
      roleId: role?.Role_ID,
      mode,
      name: name.trim(),
      userType,
      description,
      permissionKeys,
    });
  };

  const titleString = mode === "edit" ? `Edit role "${role?.Name || ""}"` : "Add role";

  const dialogContent = (
    <DialogContent>
      <div className="role-dialog-fields">
        <Text
          name="role-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter role name"
        />
        <Select
          name="role-user-type"
          label="User type"
          value={userType}
          onChange={handleUserTypeChange}
          options={userTypeOptions}
          disabled={mode === "edit"}
          placeholder="Select"
        />
        <Text
          name="role-description"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
          placeholder="Enter a description"
        />
        {loadingPermissions ? (
          <SectionLoader />
        ) : (
          <PermissionPicker
            catalog={catalog}
            userType={userType}
            value={permissionKeys}
            onChange={setPermissionKeys}
          />
        )}
      </div>
    </DialogContent>
  );

  const dialogActions = (
    <DialogActions
      action1={<CustomButton label="Cancel" onClick={onClose} variant="text" color="primary" />}
      action2={
        <CustomButton
          label="Save"
          onClick={submit}
          variant="contained"
          color="primary"
          isDisabled={!canSubmit}
        />
      }
    />
  );

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      size="sm"
      title={<DialogTitle title={titleString} />}
      content={dialogContent}
      actions={dialogActions}
    />
  );
}
